// Typed Firestore doc + collection references for every collection in
// `docs/firebase-schema.md` §§3–4. Lets feature hooks issue typed
// reads without re-typing the payload by hand.
//
// Why this layer exists.
//   - Firestore's `doc()` / `collection()` return `DocumentReference<DocumentData>`
//     and `CollectionReference<DocumentData>` by default. Without a
//     converter, every snapshot loses type information at the read
//     boundary.
//   - `withConverter` lets us register one converter per collection that
//     produces the correct shared type. We don't validate fields here
//     (`firestoreDataConverter.fromFirestore` runs after rules-permitted
//     reads, so the data is already what the rules said it would be).
//     The converter is a pure projection — its only job is the type
//     assertion.
//
// Convention.
//   - One `<Entity>Ref(stakeId, id)` for `DocumentReference`.
//   - One `<entities>Col(stakeId)` for `CollectionReference`.
//   - For top-level (cross-stake) collections, no `stakeId` arg.
//   - Callers pass the active stake (`useActiveStake()` — `null` for a
//     zero-role platform superadmin). Consumers gate their subscriptions
//     on the non-null branch.

import {
  collection,
  doc,
  type CollectionReference,
  type DocumentReference,
  type Firestore,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import type {
  Access,
  AccessRequest,
  AuditLog,
  Building,
  KindooManager,
  KindooSite,
  Organization,
  PlatformAuditLog,
  PlatformSuperadmin,
  RemoteApplyDesktop,
  RemoteApplyJob,
  RemoteApplyPresence,
  Seat,
  Stake,
  StakeSchedule,
  UserIndexEntry,
  Ward,
} from '@kindoo/shared';

// Shared converter factory. The converter is a structural projection —
// no field-level validation here; the data shape is whatever Firestore
// returned. Rules + Cloud Function code have already gated writes to
// the right shape; the runtime contract on reads is "what the rules
// said the shape would be, modulo undefined optional fields."
function passthroughConverter<T>(): FirestoreDataConverter<T> {
  return {
    toFirestore(data: T) {
      return data as unknown as { [key: string]: unknown };
    },
    fromFirestore(snapshot: QueryDocumentSnapshot): T {
      // The shape contract is enforced by rules + by Cloud Function code
      // on the write side. On reads, the SDK gives us back the exact
      // map it stored, which matches the type T by construction.
      return snapshot.data() as T;
    },
  };
}

// ---- Top-level collections (cross-stake) -----------------------------

export function userIndexRef(
  db: Firestore,
  canonicalEmail: string,
): DocumentReference<UserIndexEntry> {
  return doc(db, 'userIndex', canonicalEmail).withConverter(passthroughConverter<UserIndexEntry>());
}

export function userIndexCol(db: Firestore): CollectionReference<UserIndexEntry> {
  return collection(db, 'userIndex').withConverter(passthroughConverter<UserIndexEntry>());
}

export function platformSuperadminRef(
  db: Firestore,
  canonicalEmail: string,
): DocumentReference<PlatformSuperadmin> {
  return doc(db, 'platformSuperadmins', canonicalEmail).withConverter(
    passthroughConverter<PlatformSuperadmin>(),
  );
}

export function platformSuperadminsCol(db: Firestore): CollectionReference<PlatformSuperadmin> {
  return collection(db, 'platformSuperadmins').withConverter(
    passthroughConverter<PlatformSuperadmin>(),
  );
}

export function platformAuditLogRef(
  db: Firestore,
  auditId: string,
): DocumentReference<PlatformAuditLog> {
  return doc(db, 'platformAuditLog', auditId).withConverter(
    passthroughConverter<PlatformAuditLog>(),
  );
}

export function platformAuditLogCol(db: Firestore): CollectionReference<PlatformAuditLog> {
  return collection(db, 'platformAuditLog').withConverter(passthroughConverter<PlatformAuditLog>());
}

/**
 * `remoteApply/{canonicalEmail}` — the manager's own remote-apply
 * mailbox parent: the profile-wide opt-in and nothing else. Top-level
 * (not per-stake) because the key is the manager's canonical email and
 * the extension resolves the stake from whichever Kindoo site its tab
 * is in — stake is a field on the child docs, not a path segment.
 */
export function remoteApplyRef(
  db: Firestore,
  canonicalEmail: string,
): DocumentReference<RemoteApplyPresence> {
  return doc(db, 'remoteApply', canonicalEmail).withConverter(
    passthroughConverter<RemoteApplyPresence>(),
  );
}

/**
 * `remoteApply/{canonicalEmail}/desktops` — one doc per Kindoo site the
 * manager has a live tab on, keyed by the SBA site id. A stake can run
 * more than one Kindoo site and a tab can only provision for the site
 * it is inside, so liveness is per site rather than per manager.
 */
export function remoteApplyDesktopsCol(
  db: Firestore,
  canonicalEmail: string,
): CollectionReference<RemoteApplyDesktop> {
  return collection(db, 'remoteApply', canonicalEmail, 'desktops').withConverter(
    passthroughConverter<RemoteApplyDesktop>(),
  );
}

/** `remoteApply/{canonicalEmail}/jobs` — one doc per phone-initiated apply. */
export function remoteApplyJobsCol(
  db: Firestore,
  canonicalEmail: string,
): CollectionReference<RemoteApplyJob> {
  return collection(db, 'remoteApply', canonicalEmail, 'jobs').withConverter(
    passthroughConverter<RemoteApplyJob>(),
  );
}

export function remoteApplyJobRef(
  db: Firestore,
  canonicalEmail: string,
  jobId: string,
): DocumentReference<RemoteApplyJob> {
  return doc(db, 'remoteApply', canonicalEmail, 'jobs', jobId).withConverter(
    passthroughConverter<RemoteApplyJob>(),
  );
}

/**
 * `stakeSchedules/{stakeId}` — the stake's scheduled-task rows (D38).
 * Top-level despite being keyed by stake id: a per-stake home would put
 * it inside the slice `auditTrigger` instruments, and the hourly
 * dispatcher rewrites this doc every time it stamps a row.
 */
export function stakeScheduleRef(db: Firestore, stakeId: string): DocumentReference<StakeSchedule> {
  return doc(db, 'stakeSchedules', stakeId).withConverter(passthroughConverter<StakeSchedule>());
}

// ---- Per-stake collections ------------------------------------------

export function stakeRef(db: Firestore, stakeId: string): DocumentReference<Stake> {
  return doc(db, 'stakes', stakeId).withConverter(passthroughConverter<Stake>());
}

export function stakesCol(db: Firestore): CollectionReference<Stake> {
  return collection(db, 'stakes').withConverter(passthroughConverter<Stake>());
}

export function wardRef(db: Firestore, stakeId: string, wardCode: string): DocumentReference<Ward> {
  return doc(db, 'stakes', stakeId, 'wards', wardCode).withConverter(passthroughConverter<Ward>());
}

export function wardsCol(db: Firestore, stakeId: string): CollectionReference<Ward> {
  return collection(db, 'stakes', stakeId, 'wards').withConverter(passthroughConverter<Ward>());
}

export function buildingRef(
  db: Firestore,
  stakeId: string,
  buildingId: string,
): DocumentReference<Building> {
  return doc(db, 'stakes', stakeId, 'buildings', buildingId).withConverter(
    passthroughConverter<Building>(),
  );
}

export function buildingsCol(db: Firestore, stakeId: string): CollectionReference<Building> {
  return collection(db, 'stakes', stakeId, 'buildings').withConverter(
    passthroughConverter<Building>(),
  );
}

export function kindooManagerRef(
  db: Firestore,
  stakeId: string,
  canonicalEmail: string,
): DocumentReference<KindooManager> {
  return doc(db, 'stakes', stakeId, 'kindooManagers', canonicalEmail).withConverter(
    passthroughConverter<KindooManager>(),
  );
}

export function kindooManagersCol(
  db: Firestore,
  stakeId: string,
): CollectionReference<KindooManager> {
  return collection(db, 'stakes', stakeId, 'kindooManagers').withConverter(
    passthroughConverter<KindooManager>(),
  );
}

export function kindooSiteRef(
  db: Firestore,
  stakeId: string,
  kindooSiteId: string,
): DocumentReference<KindooSite> {
  return doc(db, 'stakes', stakeId, 'kindooSites', kindooSiteId).withConverter(
    passthroughConverter<KindooSite>(),
  );
}

export function kindooSitesCol(db: Firestore, stakeId: string): CollectionReference<KindooSite> {
  return collection(db, 'stakes', stakeId, 'kindooSites').withConverter(
    passthroughConverter<KindooSite>(),
  );
}

export function organizationRef(
  db: Firestore,
  stakeId: string,
  organizationId: string,
): DocumentReference<Organization> {
  return doc(db, 'stakes', stakeId, 'organizations', organizationId).withConverter(
    passthroughConverter<Organization>(),
  );
}

export function organizationsCol(
  db: Firestore,
  stakeId: string,
): CollectionReference<Organization> {
  return collection(db, 'stakes', stakeId, 'organizations').withConverter(
    passthroughConverter<Organization>(),
  );
}

export function accessRef(
  db: Firestore,
  stakeId: string,
  canonicalEmail: string,
): DocumentReference<Access> {
  return doc(db, 'stakes', stakeId, 'access', canonicalEmail).withConverter(
    passthroughConverter<Access>(),
  );
}

export function accessCol(db: Firestore, stakeId: string): CollectionReference<Access> {
  return collection(db, 'stakes', stakeId, 'access').withConverter(passthroughConverter<Access>());
}

export function seatRef(
  db: Firestore,
  stakeId: string,
  canonicalEmail: string,
): DocumentReference<Seat> {
  return doc(db, 'stakes', stakeId, 'seats', canonicalEmail).withConverter(
    passthroughConverter<Seat>(),
  );
}

export function seatsCol(db: Firestore, stakeId: string): CollectionReference<Seat> {
  return collection(db, 'stakes', stakeId, 'seats').withConverter(passthroughConverter<Seat>());
}

export function requestRef(
  db: Firestore,
  stakeId: string,
  requestId: string,
): DocumentReference<AccessRequest> {
  return doc(db, 'stakes', stakeId, 'requests', requestId).withConverter(
    passthroughConverter<AccessRequest>(),
  );
}

export function requestsCol(db: Firestore, stakeId: string): CollectionReference<AccessRequest> {
  return collection(db, 'stakes', stakeId, 'requests').withConverter(
    passthroughConverter<AccessRequest>(),
  );
}

export function auditLogRef(
  db: Firestore,
  stakeId: string,
  auditId: string,
): DocumentReference<AuditLog> {
  return doc(db, 'stakes', stakeId, 'auditLog', auditId).withConverter(
    passthroughConverter<AuditLog>(),
  );
}

export function auditLogCol(db: Firestore, stakeId: string): CollectionReference<AuditLog> {
  return collection(db, 'stakes', stakeId, 'auditLog').withConverter(
    passthroughConverter<AuditLog>(),
  );
}
